import { TemplatesPaths } from '@api/dashboards/Marketing/paths';
import { ProductCode, ProductType } from '@dev/ee-api/ee-api-models';

export const pathsParams = {
    productCode: ':productCode',
    user: ':user',
    subAccount: ':subAccount',
    channelName: ':channelName',
    feature: ':feature',
    template: ':template?',
};

export const DashboardPaths = {
    [ProductCode.EmailApi]: '/api',
    [ProductCode.EmailVerification]: '/evs',
    [ProductCode.Marketing]: '/marketing',
    [ProductType.API]: '/api',
    [ProductCode.CreatorSuite]: '/creator',
};

const marketingTemplatePath = '/templates/new';

export const SettingsPaths = {
    Settings: '/settings',
    SystemOptions: '/settings/system-options',
    TrackingAndPruning: '/settings/tracking-and-pruning',
    Advanced: '/settings/advanced',
    Unsubscribe: '/settings/unsubscribe',
    Domains: '/settings/domains',
    DomainsAdvanced: '/settings/domains/advanced',
    DomainsDomainVerification: '/settings/domains/domain-verification/',
    EmailAddressVerification: '/settings/domains/email-address-verification',
    DomainsEmailVerification: '/settings/domains/email-verification/',
    SingleDomainVerification: '/settings/domains/domain-verification/verify/:domainName?',
    SingleDomainBounceDomain: '/settings/domains/domain-verification/bounce-domain/:domainName',
    ManagePrivateIPs: '/settings/manage-private-ips',
    ManageInbounds: '/settings/manage-inbound-emails',
    EditInbound: '/settings/edit-inbound-email/:id',
    CreateInbound: '/settings/create-inbound-email',
    ManageWebhooks: '/settings/manage-webhooks',
    EditWebhook: '/settings/edit-webhook/:id',
    CreateWebhook: '/settings/create-webhook',
    ManageSMTP: '/settings/manage-smtp',
    ManageAPI: '/settings/manage-api',
    EditSMTP: '/settings/edit-smtp/:tokenName',
    EditAPI: '/settings/edit-api/:tokenName',
    CreateSMTP: '/settings/create-smtp',
    CreateAPI: '/settings/create-api',
    ManagePools: '/settings/manage-pools',
    SingleIP: '/settings/manage-private-ips/:singleIp',
    Billing: '/payments',
    OldSettings: '/settings',
};

export const LogsPaths = {
    Logs: '/logs/:logType',
    DetailedEmail: '/logs/:logType/:contact/:msgid',
    LogsEmail: '/logs/email',
    LogsNotification: '/logs/notification',
};

export const TemplateListPaths = {
    TemplateList: '/templates/new',
    PreviewLink: '/templates/new/preview/:name',
    EditorLink: '/templates/new/editor',
    TemplateEditorName: '/templates/new/editor/:name?',
    PreviewLinkPublic: '/templates/new/preview/public/:name',
    LegacyDragDropEditor: '/legacy/#/templates/edit',
    DragDropEditor: '/templates/edit',
};

export const BillingPaths = {
    Billing: '/billing',
    BillingPlanList: '/billing/:productCode/plans',
    BillingPlansRenewal: '/billing/renewal',
    AddPaymentMethod: '/billing/payment-methods/add',
};

export const LegacyPaths = {
    BuyLegacyIp: '/legacy#/account?buyip=true',
    Billing: '/legacy/#/payments',
    Account: '/legacy/#/account',
};

export const HomePaths = {
    Home: '/',
    Login: '/login',
    CreateAccount: '/create-account',
    ConfirmEmail: '/confirm-email',
    RegisterUser: '/register',
    ResetPassword: '/reset-password',
    Forgot: '/forgot',
    Unauthorized: `/401`,
    NotFound: '/404',
    InternalServerError: `/500`,
    SERVICE_UNAVAILABLE: '/503',
    Dashboard: '/dashboard',
    AccountDeleted: '/account-delete-confirmation',
    Security: '/security',
    Reputation: '/reputation',
    Two_FA: '/security/two-factor-authentication',
    Account: '/account/',
    Automations: '#/automations',
    // TODO: https://elasticemail.atlassian.net/browse/APP-28
    BillingNew: '/billing',
    // Billing: '#/payments',
    // Billing_noHash: '/payments',
    ContentAnalysis: '/reputation/contentanalysis',
    Help: '/help',
    Campaigns: '/campaigns',
    Templates: TemplatesPaths?.TemplateList || marketingTemplatePath,
    SubAccounts: '/sub-accounts',
    FEATURE_UPSELLING: `/upselling/${pathsParams.feature}`,
    ProfileInfo: '/personal-info',
};
export const SuppressionsPaths = {
    Suppressions: '/suppressions',
    SuppressionsAdd: '/suppressions/add',
    UnsubscribedSuppressions: '/suppressions/unsubscribed',
};
export const ContactsPaths = {
    ContactsOverview: '/contacts/new/overview',
    ContactsUpload: '/contacts/new/upload',
    ContactsHistory: '/contacts/new/history',
    CustomFields: '/contacts/new/fields',
    ContactsAdd: '/contacts/new/add',
    Segments: '/contacts/new/segments',
    SegmentsByTarget: '/contacts/new/segment/:segmentname/targets',
    SingleSegment: '/contacts/new/segment/:segmentname',
    SegmentEditor: '/contacts/new/segment/edit/:segmentname',
    SegmentCreator: '/contacts/new/segments/create',
    Lists: '/contacts/new/lists',
    ListCreator: '/contacts/new/list/create/',
    ListEditor: '/contacts/new/list/edit/:listname',
    SingleList: '/contacts/new/list/:listname',
    CampaignsByTarget: '/contacts/new/list/:listname/targets',
    ListUpload: '/contacts/new/list/:listname/upload',
    ListAdd: '/contacts/new/list/:listname/add',
    ListTargets: '/contacts/new/list/:listname/targets',
    EditList: '/contacts/new/list/edit/:listname',
    SingleContact: '/contacts/new/:contact',
    ListSingleContact: '/contacts/new/list/:listname/:contact',
    SegmentSingleContact: '/contacts/new/segment/:segmentname/:contact',
    EmailView: '/contacts/new/:contact/:msgid',
    Contacts: '/contacts/new/',
    CampaignCreatorTarget: '/campaigncreator/target/:listname',
    SuppressionUpload: SuppressionsPaths.SuppressionsAdd,
};

export const ExportsPaths = {
    Exports: '/exports',
};

export const StatisticsPaths = {
    StatisticsOverview: '/activity',
    StatisticsLog: '/activity/inprogress/log',
    StatisticsInprogress: '/activity/inprogress',
    StatisticsChannels: '/activity/channels',
    StatisticsDeviceDetails: '/activity/device-details',
    StatisticsChannelOverview: '/activity/channels/:channelName/overview',
    StatisticsChannelLogs: '/activity/channels/:channelName/logs',
    StatisticsChannelLinkTracking: '/activity/channels/:channelName/linktracking',
    StatisticsChannelLinkTrackingContacts: '/activity/channels/:channelName/linktracking/contacts',
    AllLinkTracking: '/activity/linktracking',
    AllLinkTrackingSingleLinkContacts: '/activity/linktracking/contacts',
};

export const VerificationPaths = {
    BulkVerification: '/evs/bulk',
    SingleFile: '/file/:id',
    VerificationsTabs: '/evs',
    SingleLead: '/singlelead',
};

export const UpsellingPaths = {
    MarketingUpselling: '/marketing-upselling',
    MarketingHomeUpselling: '/marketing-upselling-home',
};

export const SurveysPaths = {
    Onboarding: '/survey/onboarding',
};
